(function () {
  'use strict';

  if (window.MochilaJudgeMediaRecovery) return;

  const widgetSelector = '.jdgm-widget, .jdgm-all-reviews-widget, .jdgm-all-reviews-widget-v2025';
  const mediaContainerSelector = [
    '.jdgm-rev__pic-link',
    '.jdgm-gallery__thumbnail-wrapper'
  ].join(',');
  const inlinePopupLinkSelector = [
    '.jdgm-row-carousel .jdgm-rev__read-full[href^="#"]',
    '.jdgm-row-carousel .jdgm-rev__pic-link[href^="#"]'
  ].join(',');
  const observedWidgets = new WeakSet();
  let activeReviewModal = null;
  let reviewModalReturnFocus = null;

  function closeReviewModal() {
    if (!activeReviewModal) return;

    const modal = activeReviewModal;
    activeReviewModal = null;
    modal.remove();
    document.body.classList.remove('codex-jdgm-modal-open');
    reviewModalReturnFocus?.focus();
    reviewModalReturnFocus = null;
  }

  function openReviewModal(link, source) {
    if (!link || !source) return;
    closeReviewModal();

    const modal = document.createElement('div');
    modal.className = 'codex-jdgm-modal';
    modal.setAttribute('role', 'presentation');

    const backdrop = document.createElement('button');
    backdrop.className = 'codex-jdgm-modal__backdrop';
    backdrop.type = 'button';
    backdrop.setAttribute('aria-label', 'Close review');

    const dialog = document.createElement('section');
    dialog.className = 'codex-jdgm-modal__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', link.matches('.jdgm-rev__pic-link') ? 'Customer review photo' : 'Verified customer review');
    dialog.tabIndex = -1;

    const closeButton = document.createElement('button');
    closeButton.className = 'codex-jdgm-modal__close';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Close review');
    closeButton.innerHTML = '<span aria-hidden="true">&times;</span>';

    const content = document.createElement('div');
    content.className = 'codex-jdgm-modal__content';
    if (source.matches('.jdgm-rev')) {
      const reviewClone = source.cloneNode(true);
      reviewClone.removeAttribute('style');
      reviewClone.classList.remove('active', 'visible', 'jdgm-glider-slide', 'jdgm-left-1', 'jdgm-center', 'jdgm-right-1');
      reviewClone.querySelectorAll('.jdgm-rev__read-full').forEach(function (readMoreLink) {
        readMoreLink.remove();
      });
      reviewClone.querySelectorAll('.jdgm-rev__title, .jdgm-rev__body').forEach(function (reviewText) {
        reviewText.removeAttribute('style');
      });
      content.appendChild(reviewClone);
    } else {
      Array.from(source.children).forEach(function (child) {
        content.appendChild(child.cloneNode(true));
      });
    }

    dialog.append(closeButton, content);
    modal.append(backdrop, dialog);
    document.body.appendChild(modal);
    document.body.classList.add('codex-jdgm-modal-open');
    activeReviewModal = modal;
    reviewModalReturnFocus = link;

    backdrop.addEventListener('click', closeReviewModal);
    closeButton.addEventListener('click', closeReviewModal);
    closeButton.focus();
  }

  function prepareInlinePopupLinks(scope) {
    if (!scope || !scope.querySelectorAll) return;

    scope.querySelectorAll(inlinePopupLinkSelector).forEach(function (link) {
      link.setAttribute('aria-haspopup', 'dialog');
    });
  }

  function decorateImage(image) {
    if (!image) return;

    if (!image.getAttribute('alt') || image.getAttribute('alt') === 'User picture') {
      image.setAttribute('alt', 'Customer photo shared in a verified Mochila review');
    }

    image.setAttribute('decoding', 'async');

    if (!image.getAttribute('loading')) {
      image.setAttribute('loading', 'lazy');
    }

    const deferredSource = image.getAttribute('data-src');
    const deferredSourceSet = image.getAttribute('data-srcset');

    if (!image.getAttribute('src') && deferredSource) {
      image.setAttribute('src', deferredSource);
    }

    if (!image.getAttribute('srcset') && deferredSourceSet) {
      image.setAttribute('srcset', deferredSourceSet);
    }
  }

  function setMediaState(container, state) {
    if (!container) return;

    const isReady = state === 'ready';
    const isError = state === 'error';

    if (
      !container.classList.contains('jdgm--loading') &&
      container.classList.contains('codex-jdgm-media-ready') === isReady &&
      container.classList.contains('codex-jdgm-media-error') === isError
    ) {
      return;
    }

    container.classList.remove('jdgm--loading');
    container.classList.toggle('codex-jdgm-media-ready', isReady);
    container.classList.toggle('codex-jdgm-media-error', isError);
  }

  function markMediaReady(container, image) {
    if (!container || !image) return;

    decorateImage(image);

    if (image.complete && image.naturalWidth > 0) {
      setMediaState(container, 'ready');
    } else if (image.complete) {
      setMediaState(container, 'error');
    }
  }

  function watchImage(container, image) {
    if (!container || !image) return;

    if (container.matches('a')) {
      container.setAttribute('aria-label', 'Open customer review photo');
      container.setAttribute('rel', 'nofollow noopener');
    }

    decorateImage(image);
    markMediaReady(container, image);

    if (image.dataset.codexJudgeMediaBound === 'true') return;
    image.dataset.codexJudgeMediaBound = 'true';

    image.addEventListener('load', function () {
      markMediaReady(container, image);
    });

    image.addEventListener('error', function () {
      setMediaState(container, 'error');
    });
  }

  function scanContainer(container) {
    if (!container || !container.matches(mediaContainerSelector)) return;
    watchImage(container, container.querySelector('img'));
  }

  function scanMedia(scope) {
    if (!scope || (scope.nodeType !== 1 && scope.nodeType !== 9)) return;

    if (scope.nodeType === 1) {
      if (scope.matches(mediaContainerSelector)) scanContainer(scope);

      if (scope.matches('img')) {
        scanContainer(scope.closest(mediaContainerSelector));
      }
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(mediaContainerSelector).forEach(scanContainer);
    }
  }

  function observeWidget(widget) {
    if (!widget || observedWidgets.has(widget)) return;

    observedWidgets.add(widget);
    scanMedia(widget);
    prepareInlinePopupLinks(widget);

    if (!('MutationObserver' in window)) return;

    const widgetObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(scanMedia);
      });

      prepareInlinePopupLinks(widget);
    });

    widgetObserver.observe(widget, {
      childList: true,
      subtree: true
    });
  }

  function discoverWidgets(scope) {
    if (!scope || (scope.nodeType !== 1 && scope.nodeType !== 9)) return;

    if (scope.nodeType === 1 && scope.matches(widgetSelector)) {
      observeWidget(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(widgetSelector).forEach(observeWidget);
    }
  }

  function scanDocument() {
    discoverWidgets(document);
    scanMedia(document);
    prepareInlinePopupLinks(document);
  }

  document.addEventListener('load', function (event) {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) return;

    markMediaReady(image.closest(mediaContainerSelector), image);
  }, true);

  document.addEventListener('shopify:section:load', function (event) {
    discoverWidgets(event.target);
    scanMedia(event.target);
    prepareInlinePopupLinks(event.target);
  });

  document.addEventListener('click', function (event) {
    const link = event.target.closest?.(inlinePopupLinkSelector);
    if (!link) return;

    const source = link.matches('.jdgm-rev__pic-link') ? link : link.closest('.jdgm-rev');
    if (!source) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    document.dispatchEvent(new CustomEvent('mochila:reviews:interaction', {
      detail: { action: 'media-open', surface: 'judgeme' }
    }));
    openReviewModal(link, source);
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && activeReviewModal) {
      event.preventDefault();
      closeReviewModal();
    }
  });

  function initialize() {
    scanDocument();

    if ('MutationObserver' in window && document.body) {
      const discoveryObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            discoverWidgets(node);
            scanMedia(node);
          });
        });
      });

      discoveryObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      window.setTimeout(function () {
        discoveryObserver.disconnect();
      }, 8000);
    }

  }

  window.MochilaJudgeMediaRecovery = {
    scan: scanDocument
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
